import { Component, computed, inject, signal } from '@angular/core';
import {
  NavigationCancel, NavigationEnd, NavigationError, NavigationStart,
  Router, RouterLink, RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavBarComponent } from './shared/components/nav-bar/nav-bar.component';
import { AmbientBackdropComponent } from './shared/components/ambient-backdrop/ambient-backdrop.component';
import { BrandMarkComponent } from './shared/components/brand-mark/brand-mark.component';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { AuthService } from './core/services/auth.service';
import { EVENT_DATE_RANGE_LABEL } from './core/data/tournament-clock';
import { TournamentDataService } from './core/services/tournament-data.service';
import { BusyService } from './core/services/busy.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, NavBarComponent, AmbientBackdropComponent, BrandMarkComponent, ConfirmDialogComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private readonly router = inject(Router);
  private readonly data = inject(TournamentDataService);
  private readonly auth = inject(AuthService);

  /** What the app is saving right now, or null when idle. */
  readonly busyLabel = inject(BusyService).label;

  /**
   * The footer's Staff link points at the dashboard once someone is signed in,
   * exactly as the header's button does. On the day of the Habba the header
   * button is removed, which leaves this link as the only way between the
   * public site and the panel, so it has to work in both directions.
   */
  readonly isStaff = this.auth.isLoggedIn;

  readonly eventDates = EVENT_DATE_RANGE_LABEL;
  readonly year = new Date().getFullYear();

  /** The last attempt to load tournament data failed; the page may be showing stale or no data. */
  readonly loadFailed = computed(() => this.data.loadState() === 'error');
  readonly retrying = signal(false);

  /** Flips between two identical animations so the fade replays on every
   *  navigation — a class name that never changes would not restart it. */
  readonly routeParity = signal(0);

  /**
   * A navigation is in flight. Every route is lazily loaded, so on a weak
   * connection the gap between the tap and the new page is a chunk download
   * with nothing on screen to show for it: people concluded the link was dead
   * and tapped it again. The bar in the header is the answer to that.
   */
  readonly navigating = signal(false);

  /**
   * Anything the app is waiting on before the page can be complete: the route
   * chunk, then the first load of the data.
   *
   * Navigation alone is not enough. `NavigationEnd` fires the moment the route
   * component is constructed, which is well before its data arrives — measured
   * at ~860ms against ~1070ms locally, and the gap grows with the connection.
   * A bar that stopped at the first of those switched off while the page was
   * still visibly empty, which is what made it look decorative.
   */
  readonly working = computed(() => this.navigating() || this.data.loadState() === 'loading');

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(() => this.navigating.set(true));

    // Cancelled covers the unsaved-changes guard turning a navigation back.
    // Without it the bar would be left running after staff choose to stay.
    this.router.events
      .pipe(filter(e => e instanceof NavigationCancel || e instanceof NavigationError))
      .subscribe(() => this.navigating.set(false));

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.navigating.set(false);
        window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
        this.routeParity.update(v => (v + 1) % 2);
      });
  }

  async retryLoad(): Promise<void> {
    this.retrying.set(true);
    try {
      await this.data.loadAll();
    } finally {
      this.retrying.set(false);
    }
  }
}
